import { Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthProvider';
import { Button, Card, CenteredScreen, ErrorText, Field } from '../components/ui';
import {
  type InviteErrorCode,
  type InvitePreview,
  type InviteResult,
  callInvite,
} from '../lib/invites';

const MIN_PASSWORD_LENGTH = 12;

/** Open without an account: a person the school registered turns their one-time code into access. */
export function InvitePage({ initialCode = '' }: { initialCode?: string }) {
  const { t } = useTranslation();
  const { supabase, session, refresh } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState(initialCode);
  const [preview, setPreview] = useState<InvitePreview>();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const errorText = (code: InviteErrorCode) => {
    switch (code) {
      case 'bad_request':
      case 'server_error':
      case 'network':
        return t('common.error');
      default:
        return t(`invite.errors.${code}`);
    }
  };

  async function run<T>(request: () => Promise<InviteResult<T>>): Promise<T | undefined> {
    setBusy(true);
    setError(undefined);
    const result = await request();
    setBusy(false);
    if (result.ok) return result.data;
    setError(errorText(result.error));
    return undefined;
  }

  async function onCheck(event: FormEvent) {
    event.preventDefault();
    const found = await run(() => callInvite(supabase, { action: 'check', code }));
    if (found) setPreview(found);
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    if ([...password].length < MIN_PASSWORD_LENGTH) {
      setError(t('invite.errors.password_too_short'));
      return;
    }
    if (password !== repeat) {
      setError(t('invite.passwordMismatch'));
      return;
    }
    const accepted = await run(() =>
      callInvite(supabase, { action: 'accept', code, email, password }),
    );
    if (!accepted) return;
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (signInError) {
      setError(t('common.error'));
      return;
    }
    await navigate({ to: '/' });
  }

  async function onAttach() {
    const accepted = await run(() => callInvite(supabase, { action: 'accept', code }));
    if (!accepted) return;
    await refresh();
    await navigate({ to: '/' });
  }

  return (
    <CenteredScreen>
      <Card>
        <h1 className="mb-1 font-display text-[32px] leading-[38px] font-light tracking-[-0.04em] text-ink">
          {t('invite.title')}
        </h1>
        <p className="mb-6 text-[15px] text-muted">{t('invite.subtitle')}</p>

        {!preview ? (
          <form className="space-y-4" onSubmit={onCheck}>
            <Field
              id="invite-code"
              label={t('invite.code')}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              required
              maxLength={20}
              placeholder="ABCDE-FGHJK"
              className="font-mono tracking-widest uppercase"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <ErrorText>{error}</ErrorText>
            <Button type="submit" disabled={busy}>
              {busy ? t('invite.checking') : t('invite.check')}
            </Button>
          </form>
        ) : (
          <div className="space-y-4">
            <dl className="rounded-[20px] bg-[#F5F3EF] p-4 text-sm">
              <dt className="text-text-muted">{t('invite.school')}</dt>
              <dd className="mb-2 font-medium">{preview.school_name}</dd>
              <dt className="text-text-muted">{t('invite.role')}</dt>
              <dd className="font-medium">{t(`roles.${preview.kind}`)}</dd>
            </dl>

            {session ? (
              <>
                <p className="text-sm">
                  {t('invite.signedInAs', { email: session.user.email ?? '' })}
                </p>
                <ErrorText>{error}</ErrorText>
                <Button type="button" disabled={busy} onClick={() => void onAttach()}>
                  {busy ? t('invite.attaching') : t('invite.attach')}
                </Button>
              </>
            ) : (
              <form className="space-y-4" onSubmit={onCreate}>
                <h2 className="font-display text-[22px] leading-7 font-light tracking-[-0.02em] text-ink">
                  {t('invite.createTitle')}
                </h2>
                <Field
                  id="email"
                  label={t('auth.email')}
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Field
                  id="new-password"
                  label={t('auth.password')}
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <p className="-mt-2 text-xs text-text-muted">{t('invite.passwordHint')}</p>
                <Field
                  id="repeat-password"
                  label={t('invite.passwordRepeat')}
                  type="password"
                  autoComplete="new-password"
                  required
                  value={repeat}
                  onChange={(e) => setRepeat(e.target.value)}
                />
                <ErrorText>{error}</ErrorText>
                <Button type="submit" disabled={busy}>
                  {busy ? t('invite.creating') : t('invite.create')}
                </Button>
              </form>
            )}

            <button
              type="button"
              className="text-sm text-muted underline decoration-[#E4E0D7] underline-offset-4"
              onClick={() => {
                setPreview(undefined);
                setError(undefined);
              }}
            >
              {t('invite.otherCode')}
            </button>
          </div>
        )}

        {!session && (
          <p className="mt-6 text-center text-sm">
            <Link
              to="/"
              className="font-medium text-ink underline decoration-[#E4E0D7] decoration-2 underline-offset-4 hover:decoration-current"
            >
              {t('invite.haveAccount')}
            </Link>
          </p>
        )}
      </Card>
    </CenteredScreen>
  );
}
